import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import { ThemeContext } from '../Context';
import { ReactComponent as BlueNoteLogo } from '../../assets/logo_bluenote.svg';
import { logAction, formatReportNumber, LOG_ACTIONS } from '../../utils/logClient';
import PocketImportReview from './pocket/PocketImportReview';
import { importPocketFile, newImportId, pocketErrorMessage, previewPocketFile } from './pocket/pocketApi';
import { buildImportPayload, initialChoices, initialMappings, secretLooksComplete } from './pocket/pocketImportModel';

// Limite du format BNP1 : un fichier plus gros n'est pas lu
const MAX_POCKET_FILE_BYTES = 8 * 1024 * 1024;

const getPalette = (isDark) => (isDark ? {
  paper: '#1a252f',
  surface: '#22303e',
  border: '#2d3f4c',
  text: '#ecf0f1',
  textSecondary: '#95a5a6',
  accent: '#8c9eff',
  accentSoft: 'rgba(140, 158, 255, 0.12)',
  headerBg: 'linear-gradient(135deg, rgb(37, 53, 100), rgb(52, 74, 141))'
} : {
  paper: '#f5f7fa',
  surface: '#fff',
  border: '#dde3ec',
  text: '#212529',
  textSecondary: '#6c757d',
  accent: '#425f9e',
  accentSoft: 'rgba(102, 126, 234, 0.08)',
  headerBg: 'linear-gradient(135deg, #1a253c, #2d407a)'
});

const brandGradient = 'linear-gradient(135deg, #425f9e 0%, #667eea 100%)';

// Les applications ne sont pas encore publiées : les QR codes sont des motifs factices
const STORES = [
  {
    key: 'ios',
    name: 'App Store',
    devices: 'iPhone et iPad',
    icon: AppleIcon,
    iconColor: (isDark) => (isDark ? '#fff' : '#000'),
    qrIconColor: '#000',
    qrSeed: 20260417
  },
  {
    key: 'android',
    name: 'Google Play',
    devices: 'Téléphones Android',
    icon: AndroidIcon,
    iconColor: () => '#3ddc84',
    qrIconColor: '#3ddc84',
    qrSeed: 19870923
  }
];

const QR_MODULES = 25;

const createRandom = (seed) => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// Reproduit la structure d'un QR code version 2 (repères, motifs de synchronisation et
// d'alignement) autour de modules aléatoires
const buildPlaceholderQrPath = (seed) => {
  const random = createRandom(seed);
  const last = QR_MODULES - 1;
  let path = '';

  for (let y = 0; y < QR_MODULES; y += 1) {
    for (let x = 0; x < QR_MODULES; x += 1) {
      let dark;
      const inFinder = (x < 8 || x > last - 8) && (y < 8 || y > last - 8) && !(x > last - 8 && y > last - 8);
      if (inFinder) {
        const fx = x > last - 8 ? x - (last - 6) : x;
        const fy = y > last - 8 ? y - (last - 6) : y;
        dark = fx >= 0 && fy >= 0 && fx <= 6 && fy <= 6 && Math.max(Math.abs(fx - 3), Math.abs(fy - 3)) !== 2;
      } else if (Math.abs(x - 18) <= 2 && Math.abs(y - 18) <= 2) {
        dark = Math.max(Math.abs(x - 18), Math.abs(y - 18)) !== 1;
      } else if (x === 6 || y === 6) {
        dark = (x + y) % 2 === 0;
      } else {
        dark = random() < 0.5;
      }
      if (dark) path += `M${x} ${y}h1v1h-1z`;
    }
  }
  return path;
};

function PlaceholderQrCode({ seed, size, icon: Icon, iconColor }) {
  const path = useMemo(() => buildPlaceholderQrPath(seed), [seed]);

  return (
    <Box sx={{ position: 'relative', width: size, height: size }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${QR_MODULES} ${QR_MODULES}`}
        shapeRendering="crispEdges"
        role="img"
        aria-label="QR code de téléchargement"
      >
        <path d={path} fill="#1a253c" />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: size * 0.24,
          height: size * 0.24,
          borderRadius: '8px',
          backgroundColor: '#fff',
          boxShadow: '0 0 0 3px #fff',
          display: 'grid',
          placeItems: 'center'
        }}
      >
        <Icon sx={{ fontSize: size * 0.18, color: iconColor }} />
      </Box>
    </Box>
  );
}

const isFileDrag = (event) => Array.from(event.dataTransfer?.types || []).includes('Files');

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1).replace('.', ',')} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} Mo`;
};

function StepTitle({ number, title, subtitle, palette: c }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          background: brandGradient,
          color: '#fff',
          fontWeight: 700,
          fontSize: '0.85rem',
          boxShadow: '0 3px 10px rgba(102, 126, 234, 0.35)'
        }}
      >
        {number}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: c.text, lineHeight: 1.3, pt: 0.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" sx={{ color: c.textSecondary, mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function BlueNotePocketDialog({ open, onClose, report, reloadReportData }) {
  const { theme } = useContext(ThemeContext);
  const muiTheme = useTheme();
  const fullScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
  const isDark = theme === 'dark';
  const c = getPalette(isDark);

  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [importEnabled, setImportEnabled] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const inputRef = useRef(null);

  // Le contenu du fichier et le secret restent uniquement en mémoire, le temps de l'import
  const [secret, setSecret] = useState('');
  const [fileContent, setFileContent] = useState(null);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState('');
  const [preview, setPreview] = useState(null);
  const [choices, setChoices] = useState({});
  const [mappings, setMappings] = useState({});
  const [importId, setImportId] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [result, setResult] = useState(null);

  const resetImport = () => {
    setFile(null);
    setSecret('');
    setFileContent(null);
    setReadError('');
    setPreview(null);
    setChoices({});
    setMappings({});
    setImportId(null);
    setImportError('');
    setResult(null);
  };

  const selectFile = (selected) => {
    setFile(selected);
    setReadError('');
  };

  const handleRead = async () => {
    if (!file || !report?._id) return;
    setReadError('');
    if (file.size > MAX_POCKET_FILE_BYTES) {
      setReadError('Fichier trop volumineux pour un export BlueNote Pocket.');
      return;
    }
    setReading(true);
    try {
      const text = await file.text();
      const data = await previewPocketFile(report._id, text, secret);
      setFileContent(text);
      setPreview(data);
      setChoices(initialChoices(data));
      setMappings(initialMappings(data));
      setImportId(newImportId());
      setImportError('');
    } catch (error) {
      setReadError(pocketErrorMessage(error, 'Le fichier n’a pas pu être lu.'));
    } finally {
      setReading(false);
    }
  };

  // Après un ajout dans l'encodage : relit les personnes et intervenants du document sans perdre les choix
  const handleRefresh = async () => {
    if (!fileContent || !report?._id) return;
    setRefreshing(true);
    try {
      const data = await previewPocketFile(report._id, fileContent, secret);
      setPreview(data);
      setMappings((prev) => ({ ...initialMappings(data), ...prev }));
    } catch (error) {
      setImportError(pocketErrorMessage(error, 'Actualisation impossible.'));
    } finally {
      setRefreshing(false);
    }
  };

  const handleImport = async () => {
    if (!preview || !report?._id) return;
    setImporting(true);
    setImportError('');
    try {
      const payload = buildImportPayload({ preview, choices, mappings, importId, fileContent, secret });
      const data = await importPocketFile(report._id, payload);
      setResult(data);
      setSecret('');
      setFileContent(null);
      logAction(LOG_ACTIONS.POCKET_IMPORT, formatReportNumber(report.number, report.year), {
        elementsImportes: (data.createdRubrics || []).length,
        revisionsJointes: data.attached || 0,
        dejaRealise: data.alreadyDone === true
      });
      await reloadReportData?.();
    } catch (error) {
      setImportError(pocketErrorMessage(error, 'L’import n’a pas pu être réalisé.'));
    } finally {
      setImporting(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    // Un import en cours de revue est conservé (ex. ajout d'une personne dans l'encodage entre-temps)
    if (!preview || result) resetImport();
    setDragging(false);
    setImportEnabled(false);
    setStatusLoading(true);

    let cancelled = false;
    fetch('/api/pocketsettings/public-status')
      .then((response) => (response.ok ? response.json() : { importEnabled: false }))
      .then((data) => {
        if (!cancelled) setImportEnabled(data.importEnabled === true);
      })
      .catch(() => {
        if (!cancelled) setImportEnabled(false);
      })
      .finally(() => {
        if (!cancelled) setStatusLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const openFilePicker = () => inputRef.current?.click();

  const handleFileInput = (event) => {
    const selected = event.target.files?.[0];
    if (selected) selectFile(selected);
    // Permet de sélectionner à nouveau le même fichier
    event.target.value = '';
  };

  const handleDragOver = (event) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  };

  const handleDragLeave = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setDragging(false);
  };

  const handleDrop = (event) => {
    if (!isFileDrag(event)) return;
    event.preventDefault();
    setDragging(false);
    const dropped = event.dataTransfer.files?.[0];
    if (dropped) selectFile(dropped);
  };

  // Un fichier lâché à côté de la zone de dépôt serait ouvert par le navigateur, qui quitterait l'application
  const blockStrayFileDrop = (event) => {
    if (!isFileDrag(event) || event.defaultPrevented) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'none';
  };

  const handleDropZoneKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openFilePicker();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={fullScreen}
      onDragOver={blockStrayFileDrop}
      onDrop={blockStrayFileDrop}
      PaperProps={{
        sx: {
          backgroundColor: c.paper,
          backgroundImage: 'none',
          color: c.text,
          borderRadius: fullScreen ? 0 : '20px',
          overflow: 'hidden',
          boxShadow: isDark
            ? '0 20px 60px rgba(0, 0, 0, 0.6)'
            : '0 20px 60px rgba(26, 37, 60, 0.35)'
        }
      }}
      slotProps={{
        backdrop: {
          sx: { backdropFilter: 'blur(4px)', backgroundColor: 'rgba(15, 23, 42, 0.55)' }
        }
      }}
    >
      <Box
        sx={{
          position: 'relative',
          overflow: 'hidden',
          flexShrink: 0,
          background: c.headerBg,
          px: { xs: 2.5, sm: 3.5 },
          py: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '-70px',
            right: '-30px',
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(102, 126, 234, 0.45), transparent 70%)'
          }}
        />
        <Box
          sx={{
            position: 'relative',
            width: 56,
            height: 56,
            borderRadius: '16px',
            flexShrink: 0,
            display: 'grid',
            placeItems: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.12)',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}
        >
          <BlueNoteLogo style={{ height: '34px', width: 'auto', color: 'white' }} />
          <Box
            sx={{
              position: 'absolute',
              right: -8,
              bottom: -6,
              px: 0.6,
              borderRadius: '6px',
              background: brandGradient,
              color: '#fff',
              fontSize: '0.6rem',
              fontWeight: 800,
              letterSpacing: '0.02em',
              lineHeight: 1.6,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
          >
            Pocket
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, zIndex: 1 }}>
          <Typography sx={{ color: 'white', fontWeight: 700, fontSize: { xs: '1.15rem', sm: '1.35rem' }, lineHeight: 1.25 }}>
            Importer depuis BlueNote Pocket
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mt: 0.5 }}>
            BlueNote Pocket est une application permettant de débuter votre procès-verbal directement sur le terrain.
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Fermer"
          sx={{
            alignSelf: 'flex-start',
            zIndex: 1,
            color: 'rgba(255, 255, 255, 0.85)',
            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.12)' }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 3, overflowY: 'auto' }}>
        {preview ? (
          <PocketImportReview
            preview={preview}
            choices={choices}
            setChoices={setChoices}
            mappings={mappings}
            setMappings={setMappings}
            onRefresh={handleRefresh}
            refreshing={refreshing}
            onImport={handleImport}
            importing={importing}
            importError={importError}
            result={result}
            onClose={onClose}
            onRestart={resetImport}
            palette={c}
          />
        ) : (
        <>
        <StepTitle
          number={1}
          title="Installez BlueNote Pocket sur votre téléphone"
          subtitle="Scannez le QR code correspondant à votre appareil avec son appareil photo."
          palette={c}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 2,
            mb: 3.5
          }}
        >
          {STORES.map(({ key, name, devices, icon: Icon, iconColor, qrIconColor, qrSeed }) => (
            <Box
              key={key}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                p: 2.5,
                borderRadius: '16px',
                backgroundColor: c.surface,
                border: `1px solid ${c.border}`,
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: isDark
                    ? '0 10px 24px rgba(0, 0, 0, 0.35)'
                    : '0 10px 24px rgba(26, 37, 60, 0.1)'
                }
              }}
            >
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '14px',
                  backgroundColor: '#fff',
                  border: isDark ? 'none' : `1px solid ${c.border}`
                }}
              >
                <PlaceholderQrCode seed={qrSeed} size={148} icon={Icon} iconColor={qrIconColor} />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Icon sx={{ fontSize: 30, color: iconColor(isDark) }} />
                <Box>
                  <Typography sx={{ fontWeight: 700, color: c.text, lineHeight: 1.2 }}>
                    {name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: c.textSecondary }}>
                    {devices}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ))}
        </Box>

        <StepTitle
          number={2}
          title="Importez le fichier exporté depuis BlueNote Pocket"
          palette={c}
        />
        {statusLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} sx={{ color: c.accent }} />
          </Box>
        ) : importEnabled ? (
        <Box
          role="button"
          tabIndex={0}
          aria-label="Choisir un fichier à importer"
          onClick={openFilePicker}
          onKeyDown={handleDropZoneKeyDown}
          onDragEnter={handleDragOver}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 1,
            px: 3,
            py: 4,
            borderRadius: '16px',
            border: `2px dashed ${dragging ? c.accent : c.border}`,
            backgroundColor: dragging ? c.accentSoft : c.surface,
            cursor: 'pointer',
            transition: 'border-color 0.2s ease, background-color 0.2s ease, transform 0.2s ease',
            transform: dragging ? 'scale(1.01)' : 'none',
            '&:hover': { borderColor: c.accent },
            '&:focus-visible': { outline: `2px solid ${c.accent}`, outlineOffset: '2px' }
          }}
        >
          <Box
            sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              mb: 0.5,
              backgroundColor: c.accentSoft,
              color: c.accent,
              transition: 'transform 0.2s ease',
              transform: dragging ? 'translateY(-4px)' : 'none'
            }}
          >
            <CloudUploadOutlinedIcon sx={{ fontSize: 32 }} />
          </Box>
          <Typography sx={{ fontWeight: 600, color: c.text }}>
            {dragging ? 'Relâchez pour déposer le fichier' : 'Glissez-déposez votre fichier ici'}
          </Typography>
          <Typography variant="body2" sx={{ color: c.textSecondary }}>
            ou
          </Typography>
          <Button
            variant="contained"
            disableElevation
            onClick={(event) => {
              event.stopPropagation();
              openFilePicker();
            }}
            sx={{
              borderRadius: '12px',
              px: 3,
              textTransform: 'none',
              fontWeight: 600,
              background: brandGradient,
              boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #35508c 0%, #5468d4 100%)',
                boxShadow: '0 6px 18px rgba(102, 126, 234, 0.5)'
              }
            }}
          >
            Parcourir…
          </Button>
        </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              gap: 1.5,
              px: 3,
              py: 4,
              borderRadius: '16px',
              border: `2px dashed ${c.border}`,
              backgroundColor: c.surface
            }}
          >
            <Box
              sx={{
                width: 60,
                height: 60,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                backgroundColor: c.accentSoft,
                color: c.textSecondary
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography sx={{ fontWeight: 600, color: c.text, maxWidth: 460 }}>
              Cette option n&apos;est pas activée avec votre licence BlueNote.
            </Typography>
          </Box>
        )}
        {importEnabled && <input ref={inputRef} type="file" accept=".bnp" hidden onChange={handleFileInput} />}

        {importEnabled && file && (
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.25,
              borderRadius: '12px',
              backgroundColor: c.surface,
              border: `1px solid ${c.border}`
            }}
          >
            <InsertDriveFileOutlinedIcon sx={{ color: c.accent }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600, color: c.text }}>
                {file.name}
              </Typography>
              <Typography variant="caption" sx={{ color: c.textSecondary }}>
                {formatFileSize(file.size)}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setFile(null)}
              aria-label="Retirer le fichier"
              sx={{ color: c.textSecondary }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {importEnabled && file && (
          <Box sx={{ mt: 2.5 }}>
            <StepTitle
              number={3}
              title="Saisissez le code affiché sur le téléphone"
              subtitle="Le code à 6 chiffres est communiqué séparément du fichier. Il n'est jamais enregistré par BlueNote."
              palette={c}
            />
            <Box
              component="form"
              onSubmit={(event) => {
                event.preventDefault();
                handleRead();
              }}
              sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', flexWrap: 'wrap' }}
            >
              <TextField
                value={secret}
                onChange={(event) => setSecret(event.target.value)}
                label="Code de l'export"
                placeholder="123 456"
                autoComplete="off"
                size="small"
                InputProps={{ startAdornment: <KeyOutlinedIcon sx={{ mr: 1, color: c.textSecondary }} /> }}
                inputProps={{
                  spellCheck: false,
                  autoCapitalize: 'characters',
                  'aria-label': "Code de l'export",
                  style: { fontFamily: 'monospace', letterSpacing: '0.08em', textTransform: 'uppercase' }
                }}
                sx={{ flex: '1 1 320px' }}
              />
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={!secretLooksComplete(secret) || reading || !report?._id}
                sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 600, background: brandGradient, minWidth: 150 }}
              >
                {reading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Lire le fichier'}
              </Button>
            </Box>
            {readError && <Alert severity="error" sx={{ mt: 1.5 }}>{readError}</Alert>}
          </Box>
        )}
        </>
        )}
      </Box>
    </Dialog>
  );
}

export default BlueNotePocketDialog;
